import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import type { PromptProfile } from '@/lib/api/types';

interface UsePromptProfilesOptions {
  enabled?: boolean;
}

/**
 * Shared query for prompt profiles across UI surfaces.
 * Prevents duplicate network requests when multiple screens need the same data.
 *
 * @param selectedOrgIdOverride - Org id to use when `apiClientOverride` is passed
 *   (embedded mode resolves auth through UnifiedAuthProvider).
 *   Without this, an embedded caller could never enable the query — passing
 *   apiClientOverride alone previously forced selectedOrgId to null.
 */
export function usePromptProfiles(
  options: UsePromptProfilesOptions = {},
  apiClientOverride?: ReturnType<typeof useAuth>['apiClient'],
  selectedOrgIdOverride?: string | null
) {
  const { enabled = true } = options;
  const auth = useAuth();
  const selectedOrgId = apiClientOverride ? (selectedOrgIdOverride ?? null) : auth.orgId;
  const apiClient = apiClientOverride ?? auth.apiClient;

  return useQuery({
    queryKey: ['prompt-profiles', selectedOrgId] as const,
    queryFn: async (): Promise<PromptProfile[]> => {
      const response = await apiClient.settings.getPromptProfiles();
      return response.profiles ?? [];
    },
    enabled: enabled && !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
